import isPlainObject from 'is-plain-obj'
import React from 'react'
import {useLocation, useNavigate} from 'react-router-dom'

import {OtpType} from '~/api/auth/register'
import type {APIOtpValidateResponse} from '~/auth/otp/api'
import {useHandleLoginState} from '~/shell/session'
import {useUser} from '~/shell/user'

import type {OnboardingScreenName} from '../sca'

/**
 * Custom hook to get tokens from location hash
 */
function useOAuthCallbackParams() {
    const {hash} = useLocation()
    const params = new URLSearchParams(hash.slice(1))
    const loginToken = params.get('login_token')
    const email = params.get('email')
    return {loginToken, email}
}

export enum LoginScreen {
    EMAIL = 'emailScreen',
    PASSWORD = 'passwordScreen',
    PASSCODE = 'passcodeScreen',
    OTP_EMAIL_VALIDATION = 'otpEmailValidationScreen',
    EMAIL_VALIDATION = 'emailValidationScreen',
    LOADING = 'loadingScreen',
}

type LoginLocationState = APIOtpValidateResponse & {
    otp: string | null // 6-Digit number sent to a verified phone number in possession of the user
    email: string | null // User's email.
    isLegacy?: boolean // User's login type. If there flagged for 2FA, this will be false or undefined
    passcode: string | null // User's passcode. This is not needed for SSO login
    loginToken: string | null // Token gotten from an SSO redirect. This is required for SSO login
    trust: boolean // Indicates if the device is trusted
    phoneLastDigits?: string // Last 4 digits of the user's phone number
    googleAuth?: boolean // Indicates if the user has enabled Google Auth for 2FA
    redirect?: string | null // Will redirect to this path after login
    screen: LoginScreen
    otpType: OtpType
    accountLockedTimeout?: number // indicates whether account is blocked and for how long
}

/**
 * Custom hook to use location state for the login page.
 * This is used instead of local state as a cleaner way to retain state as you go through the login flow
 */
export function useLoginLocationState() {
    useHandleLoginState()
    const location = useLocation()
    const navigate = useNavigate()
    const oauthParams = useOAuthCallbackParams()
    const state = (location.state || {
        trust: false,
        otp: null,
        email: null,
        isLegacy: undefined,
        passcode: null,
        loginToken: null,
        phoneLastDigits: null,
        googleAuth: null,
        screen: LoginScreen.EMAIL,
        otpType: OtpType.PHONE,
        accountLockedTimeout: undefined,
    }) as LoginLocationState

    React.useEffect(() => {
        if (oauthParams.loginToken) {
            // This is to clean the url after we've gotten the login params
            navigate(location.pathname, {state: {...state, ...oauthParams}, replace: true})
        }
    }, [oauthParams.loginToken])

    function update(payload: Partial<LoginLocationState>, replace = false) {
        navigate('/login', {
            replace,
            state: {...(isPlainObject(location.state) ? location.state : {}), ...payload},
        })
    }

    function clear() {
        navigate(state.redirect ?? '/login', {})
    }

    return {state, update, clear}
}

type ScaOnboardingLocationState = {
    otp: string | null // 6-Digit number sent to a verified phone number in possession of the user
    email: string | null // User's email.
    passcode: string | null // User's passcode. This is not needed for SSO login
    phone: string | null // User's phone number. This is the one being verified in this stage
    trust: boolean // Indicates if the device is trusted
    redirect: string // This is to send the user back to whereever they came from.
    phoneLastDigits: string | null
    fromScaOnboarding?: boolean
    otpType: OtpType
    currentScreen: OnboardingScreenName
}

/**
 * Custom hook to use location state for the SCA onboarding page.
 * This is used instead of local state as a cleaner way to retain state as you go through the login flow
 */
export function useScaOnboardingLocationState() {
    const user = useUser()
    const email = user?.email
    const location = useLocation()
    const navigate = useNavigate()
    const state = (location.state || {
        trust: false,
        otp: null,
        phone: null,
        passcode: null,
        phoneLastDigits: null,
        currentScreen: 'welcomeScreen',
        otpType: OtpType.PHONE,
    }) as ScaOnboardingLocationState

    function update(payload: Partial<ScaOnboardingLocationState>) {
        navigate('/secure-your-account', {
            state: {email, ...(isPlainObject(location.state) ? location.state : {}), ...payload},
        })
    }
    function clear() {
        navigate('/secure-your-account', {state: {}})
    }

    return {state, update, clear}
}
